import yaml from "js-yaml";
import { ContractCenterApi_batchSearchContract } from "@next-api-sdk/next-builder-sdk";
import { hasOwnProperty } from "@next-core/utils/general";
import { getContract } from "./CollectContracts.js";
import { isUnauthenticatedError } from "@next-core/runtime";
const remoteContractCache = new Map();

// Legacy Custom API: `${namespace}@${name}`
// Flow API: `${namespace}@${name}:${version}`
export function isFlowApiProvider(provider) {
  return provider.includes("@");
}
export async function getArgsOfFlowApi(provider, originalArgs, method, stream) {
  if (!provider.includes(":")) {
    throw new Error(`You're using legacy Custom API "${provider}" which is dropped in v3, please use Flow API instead`);
  }
  const apiDefinition = await fetchFlowApiDefinition(provider);
  if (!apiDefinition) {
    throw new FlowApiNotFoundError(`Flow API not found: "${provider}"`);
  }
  const apiProfile = getApiProfileFromApiDefinition(provider, apiDefinition);
  return getApiArgsFromApiProfile(apiProfile, originalArgs, method, stream);
}
function getApiArgsFromApiProfile(api, originalArgs, method, stream) {
  const {
    uri,
    method: apiMethod,
    ext_fields,
    responseWrapper,
    isFileType,
    request
  } = api;
  // `saveAs` requires the first argument to be the filename.
  const isDownload = method === "saveAs";
  let filename;
  let fixedArgs = originalArgs;
  if (isDownload) {
    if (Array.isArray(originalArgs)) {
      fixedArgs = originalArgs.slice();
      filename = fixedArgs.shift();
    } else {
      filename = originalArgs.filename;
    }
  }
  const {
    url,
    args
  } = getTransformedUriAndRestArgs(api, fixedArgs);
  return [...(isDownload ? [filename] : []), {
    url,
    originalUri: uri,
    method: apiMethod,
    ext_fields,
    responseWrapper,
    request,
    isFileType,
    stream
  }, ...args];
}
function getTransformedUriAndRestArgs(api, originalArgs) {
  const {
    uri,
    name,
    namespace,
    serviceName,
    version,
    method = "GET",
    request,
    ext_fields
  } = api;
  const prefix = version ? serviceName === "logic.api.gateway" || serviceName !== null && serviceName !== void 0 && serviceName.startsWith("logic.api.gateway.") ? "" : serviceName ? `api/gateway/${serviceName}` : `api/gateway/${namespace}.${name}@${version}` : `api/gateway/api_service.${namespace}.${name}`;
  let transformedUri;
  let restArgs = [];
  if (Array.isArray(originalArgs)) {
    restArgs = originalArgs.slice();
    transformedUri = uri.replace(/:([^/]+)/g, () => restArgs.shift());
  } else {
    const restParams = {
      ...originalArgs.params
    };
    transformedUri = uri.replace(/:([^/]+)/g, (_, key) => {
      if (hasOwnProperty(restParams, key)) {
        const value = restParams[key];
        delete restParams[key];
        return value;
      }
      throw new Error(`Missing required param: "${key}"`);
    });
    const isSimpleRequest = ["get", "delete", "head"].includes(method.toLowerCase());
    if (isSimpleRequest) {
      var _uri$match$length, _uri$match, _request$fields$lengt, _request$fields;
      const noParams = (request === null || request === void 0 ? void 0 : request.type) === "object" && ((_uri$match$length = (_uri$match = uri.match(/:([^/]+)/g)) === null || _uri$match === void 0 ? void 0 : _uri$match.length) !== null && _uri$match$length !== void 0 ? _uri$match$length : 0) === ((_request$fields$lengt = (_request$fields = request.fields) === null || _request$fields === void 0 ? void 0 : _request$fields.length) !== null && _request$fields$lengt !== void 0 ? _request$fields$lengt : 0);
      if (!noParams) {
        restArgs.push(restParams);
      }
    } else {
      const bodyField = ext_fields === null || ext_fields === void 0 ? void 0 : ext_fields.find(item => item.source === "body");
      if (bodyField) {
        let body;
        if (bodyField.name && hasOwnProperty(restParams, bodyField.name)) {
          body = restParams[bodyField.name];
          delete restParams[bodyField.name];
        }
        restArgs.push(body);
      }
      const queryField = ext_fields === null || ext_fields === void 0 ? void 0 : ext_fields.find(item => item.source === "query");
      if (queryField) {
        let query;
        if (queryField.name && hasOwnProperty(restParams, queryField.name)) {
          query = restParams[queryField.name];
          delete restParams[queryField.name];
        }
        restArgs.push(query);
      }
      if (!bodyField && !queryField) {
        restArgs.push(restParams);
      }
    }
    if (originalArgs.options) {
      restArgs.push(originalArgs.options);
    }
  }
  return {
    url: prefix ? prefix + transformedUri : transformedUri.replace(/^\//, ""),
    args: restArgs
  };
}
function getApiProfileFromApiDefinition(provider, api) {
  var _contract$endpoint, _contract$response;
  const contract = typeof api.contract === "string" ? yaml.safeLoad(api.contract, {
    schema: yaml.JSON_SCHEMA,
    json: true
  }) : api.contract;
  const {
    uri,
    method = "GET",
    ext_fields
  } = (_contract$endpoint = contract === null || contract === void 0 ? void 0 : contract.endpoint) !== null && _contract$endpoint !== void 0 ? _contract$endpoint : {};
  const responseWrapper = contract !== null && contract !== void 0 && contract.response ? contract.response.wrapper !== false : false;
  if (!uri) {
    throw new Error(`Missing endpoint.uri in contract of provider "${provider}"`);
  }
  return {
    uri,
    method: method.toLowerCase() === "list" ? "get" : method,
    ext_fields,
    name: api.name,
    namespace: api.namespace,
    serviceName: api.serviceName,
    version: api.version,
    isFileType: (contract === null || contract === void 0 || (_contract$response = contract.response) === null || _contract$response === void 0 ? void 0 : _contract$response.type) === "file",
    responseWrapper,
    request: contract === null || contract === void 0 ? void 0 : contract.request
  };
}
async function fetchFlowApiDefinition(provider) {
  const [namespaceName, nameWithVersion] = provider.split("@");
  const [name, version] = nameWithVersion.split(":");

  // Do not cache the result of `getContract`, which will lead to no contract
  // will be found when render twice immediately.
  const contract = getContract(`${namespaceName}.${name}`);
  if (contract) {
    return {
      name: contract.name,
      namespace: contract.namespaceId,
      serviceName: contract.serviceName,
      version: contract.version,
      contract: {
        endpoint: contract.endpoint,
        response: contract.response,
        request: contract.request
      }
    };
  }
  let promise = remoteContractCache.get(provider);
  if (!promise) {
    promise = fetchFlowApiDefinitionFromRemote(namespaceName, name, version);
    remoteContractCache.set(provider, promise);
  }
  return promise;
}
async function fetchFlowApiDefinitionFromRemote(namespace, name, version) {
  let contractData;
  const fullContractName = `${namespace}@${name}`;
  let error;
  try {
    const {
      list: contractList
    } = await ContractCenterApi_batchSearchContract({
      contract: [{
        fullContractName,
        version
      }]
    });
    contractData = contractList[0];
  } catch (e) {
    if (isUnauthenticatedError(e)) {
      // If unauthenticated, throw the error to be handled by upper level
      throw e;
    }
    error = e;
  }
  if (!contractData) {
    // eslint-disable-next-line no-console
    console.error(`Failed to fetch Flow API definition for "${fullContractName}:${version}"`, error !== null && error !== void 0 ? error : "Contract not found");
    return null;
  }
  return {
    name: contractData.name,
    namespace: contractData.namespaceId,
    serviceName: contractData.serviceName,
    version: contractData.version,
    contract: {
      endpoint: contractData.endpoint,
      response: contractData.response,
      request: contractData.request
    }
  };
}
class FlowApiNotFoundError extends Error {
  constructor(message) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(message);
    this.name = "FlowApiNotFoundError";

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    // istanbul ignore else
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FlowApiNotFoundError);
    }
  }
}
//# sourceMappingURL=FlowApi.js.map